﻿import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/solid-router";
import type { JSX } from "solid-js";
import { createSignal, Index, onMount, Show } from "solid-js";
import { toast } from "solid-sonner";
import { z } from "zod";
import { LocationMap } from "~/components/bank-transaction/location-map";
import {
  IconArrowRight,
  IconArrowRightFromLine,
  IconCircleCheck,
  IconCircleDollarSign,
  IconCircleX,
  IconClock,
  IconGlobe,
  IconHouse,
  IconInfo,
  IconLink,
  IconMapPin,
  IconPencil,
  IconStore,
  IconTag,
  IconTrash,
  IconUndo,
} from "~/components/icons";
import { JournalEntryLinesEditor } from "~/components/journal-entry/journal-entry-lines-editor";
import { NotFound } from "~/components/not-found";
import { StandardRouteAssociationsPanel } from "~/components/standard-route/detail/associations-panel";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailEditCard } from "~/components/standard-route/detail/edit-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
  StandardRouteDetailTabs,
  StandardRouteDetailTabsContent,
  StandardRouteDetailTabsStack,
} from "~/components/standard-route/detail/layout";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { Col, Grid } from "~/components/ui/grid";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Separator } from "~/components/ui/separator";
import { TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  TextField,
  TextFieldErrorMessage,
  TextFieldInput,
  TextFieldLabel,
} from "~/components/ui/text-field";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { AccountsAllResource } from "~/features/account/account.resource";
import { prefetchAssociations } from "~/features/association/association.prefetch";
import {
  getBankTransactionDetailPageData,
  type BankTransactionDetailPageData,
} from "~/features/bank-transaction/bank-transaction-detail-page";
import {
  deleteBankTransaction,
  unapproveBankTransaction,
} from "~/features/bank-transaction/bank-transaction.api";
import { useBankTransactionForm } from "~/features/bank-transaction/bank-transaction.form";
import { BankTransactionDetailResource } from "~/features/bank-transaction/bank-transaction.resource";
import { bankTransactionStatusLabel } from "~/features/bank-transaction/bank-transaction.schema";
import type {
  BankTransactionItem,
  BankTransactionLineItem,
} from "~/features/bank-transaction/bank-transaction.server";
import {
  pfcConfidenceLabel,
  pfcDetailedLabel,
  pfcPrimaryLabel,
} from "~/features/bank-transaction/pfc-taxonomy";
import {
  businessDetailResource,
  businessesResource,
} from "~/features/business/business.resource";
import { withBusinessGuard } from "~/lib/route-guards";
import { extractErrorMessage } from "~/lib/server-fn-error";
import { formatCurrency, formatDisplayCalendarDate } from "~/lib/utils";

export const Route = createFileRoute(
  "/_dashboard/dashboard/bank-transactions/$transactionId",
)({
  staticData: {
    breadcrumb: (m) =>
      m.loaderData?.bankTransaction?.name ?? "Bank Transaction",
  },
  validateSearch: z.object({
    mode: z.enum(["edit"]).optional(),
  }),
  // Route-specific 404 message (takes precedence over the dashboard-wide
  // notFoundComponent). Shown when a bank transaction doesn't exist — e.g. it
  // was deleted, or an AI agent linked here by mistake.
  notFoundComponent: () => (
    <NotFound
      title="Bank transaction not found"
      message="This bank transaction doesn't exist. It may have been deleted, or an agent may have linked you here by mistake."
      homeTo="/dashboard/bank-transactions"
      homeLabel="Go to bank transactions"
    />
  ),
  ...withBusinessGuard(),
  loader: async ({ params, context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;

    const [pageData, allAccounts] = await Promise.all([
      params.transactionId !== "new"
        ? (getBankTransactionDetailPageData({
            data: {
              businessId,
              bankTransactionId: params.transactionId,
            },
          }) as Promise<BankTransactionDetailPageData>)
        : Promise.resolve(null),
      context.queryClient.ensureQueryData(
        AccountsAllResource.options({ businessId }),
      ),
    ]);

    const businessDetail = pageData?.businessDetail ?? null;
    const bankTransaction = pageData?.bankTransaction ?? null;

    if (bankTransaction) {
      // Seed the resource cache so the detail query resolves from cache on
      // first render (mirrors the document detail page pattern).
      BankTransactionDetailResource.seed(
        context.queryClient,
        { businessId, id: params.transactionId },
        bankTransaction,
      );
    } else if (params.transactionId === "new") {
      context.queryClient.setQueryData(
        BankTransactionDetailResource.key({
          businessId,
          id: params.transactionId,
        }),
        null,
      );
    }

    // Prefetch the detail page's component-level queries so they resolve from
    // cache on mount instead of flashing loading states. Fire-and-forget (not
    // awaited) so it never blocks the loader's critical path or SSR.
    if (params.transactionId !== "new") {
      prefetchAssociations(
        context.queryClient,
        businessId,
        "bank-transaction",
        params.transactionId,
      );
    }

    return { businessId, businessDetail, bankTransaction, allAccounts };
  },
  component: BankTransactionDetailPage,
});

type Mode = "view" | "edit";

// -- Page ----------------------------------------------------------------------

function BankTransactionDetailPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = Route.useParams();
  const data = Route.useLoaderData();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().businessDetail,
  );
  AccountsAllResource.seedIfEmpty(
    qc,
    { businessId: data().businessId },
    data().allAccounts,
  );
  if (data().bankTransaction) {
    BankTransactionDetailResource.seedIfEmpty(
      qc,
      { businessId: data().businessId, id: params().transactionId },
      data().bankTransaction!,
    );
  } else if (params().transactionId === "new") {
    // Seed null into QueryClient for "new" to prevent SSR deferred stream hang
    const newKey = BankTransactionDetailResource.key({
      businessId: data().businessId,
      id: params().transactionId,
    });
    if (qc.getQueryData(newKey) === undefined) {
      qc.setQueryData(newKey, null);
    }
  }

  const isNew = () => params().transactionId === "new";
  const search = Route.useSearch();
  const initialMode: Mode = search().mode === "edit" ? "edit" : "view";
  const [mode, setMode] = createSignal<Mode>(initialMode);

  onMount(() => {
    if (initialMode === "edit") {
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  });

  const bankTransactionQuery = createQuery(() => ({
    ...BankTransactionDetailResource.options(
      { businessId: data().businessId, id: params().transactionId },
      !isNew(),
    ),
    placeholderData: keepPreviousData,
  }));

  return (
    <div
      class={`flex flex-1 flex-col px-4 sm:px-6 pb-3 pt-10 ${
        !isNew() && mode() === "view"
          ? "@5xl/main:h-[calc(100svh-4.5rem)] @5xl/main:max-h-[calc(100svh-4.5rem)] @5xl/main:min-h-0 @5xl/main:overflow-auto"
          : ""
      }`}
    >
      <Show when={isNew()}>
        <BankTransactionEdit
          bankTransaction={null}
          businessId={data().businessId}
          allAccounts={data().allAccounts}
          onCancel={() => navigate({ to: "/dashboard/bank-transactions" })}
          onSaved={() => navigate({ to: "/dashboard/bank-transactions" })}
        />
      </Show>
      <Show when={!isNew()}>
        <Show
          when={bankTransactionQuery.data}
          fallback={
            <div role="status" class="w-full animate-pulse">
              <div class="h-150 bg-muted rounded-lg w-full" />
            </div>
          }
        >
          {(bankTransaction) => (
            <Show
              when={mode() === "edit"}
              fallback={
                <BankTransactionView
                  bankTransaction={bankTransaction()}
                  businessId={data().businessId}
                  setMode={setMode}
                  onApproved={() => {
                    BankTransactionDetailResource.invalidate(qc, {
                      businessId: data().businessId,
                      id: params().transactionId,
                    });
                    // Refresh the list cache so the status badge updates.
                    qc.invalidateQueries({
                      queryKey: ["bank-transaction", data().businessId],
                    });
                  }}
                  onDeleted={() => {
                    BankTransactionDetailResource.remove(qc, {
                      businessId: data().businessId,
                      id: params().transactionId,
                    });
                    // Remove the list cache so the table doesn't flash the
                    // deleted row before a background refetch.
                    qc.removeQueries({
                      queryKey: ["bank-transaction", data().businessId],
                    });
                    navigate({ to: "/dashboard/bank-transactions" });
                  }}
                />
              }
            >
              <BankTransactionEdit
                bankTransaction={bankTransaction()}
                businessId={data().businessId}
                allAccounts={data().allAccounts}
                onCancel={() => setMode("view")}
                onSaved={() => {
                  BankTransactionDetailResource.invalidate(qc, {
                    businessId: data().businessId,
                    id: params().transactionId,
                  });
                  setMode("view");
                }}
              />
            </Show>
          )}
        </Show>
      </Show>
    </div>
  );
}

// -- Delete Dialog -------------------------------------------------------------

function DeleteBankTransactionDialog(props: {
  bankTransaction: BankTransactionItem;
  businessId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const [isDeleting, setIsDeleting] = createSignal(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await deleteBankTransaction({
        data: {
          id: props.bankTransaction.id,
          businessId: props.businessId,
        },
      });
      props.onDeleted();
    } catch {
      toast.error("Failed to delete bank transaction");
    } finally {
      setIsDeleting(false);
      props.onOpenChange(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Bank Transaction</DialogTitle>
          <DialogDescription>
            This will permanently delete{" "}
            <span class="font-semibold">"{props.bankTransaction.name}"</span>.
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => props.onOpenChange(false)}
            disabled={isDeleting()}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting()}
          >
            <IconTrash class="mr-2 size-4" />
            {isDeleting() ? "Deleting..." : "Delete Bank Transaction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -- View Mode ----------------------------------------------------------------

function BankTransactionView(props: {
  bankTransaction: BankTransactionItem;
  businessId: string;
  setMode: (mode: Mode) => void;
  onDeleted: () => void;
  onApproved: () => void;
}) {
  const [deleteOpen, setDeleteOpen] = createSignal(false);
  const [isApproving, setIsApproving] = createSignal(false);

  const isApproved = () => props.bankTransaction.status === "approved";

  async function handleUnapprove() {
    setIsApproving(true);
    try {
      await unapproveBankTransaction({
        data: {
          id: props.bankTransaction.id,
          businessId: props.businessId,
        },
      });
      toast.success("Bank transaction unapproved — journal entry removed");
      props.onApproved();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to unapprove bank transaction",
      );
    } finally {
      setIsApproving(false);
    }
  }

  const bankAccountLabel = () => {
    const { bankAccountName, bankAccountMask } = props.bankTransaction;
    if (!bankAccountName && !bankAccountMask) return "—";
    return bankAccountMask
      ? `${bankAccountName ?? ""} •••• ${bankAccountMask}`
      : (bankAccountName ?? "—");
  };

  return (
    <div class="flex flex-1 flex-col gap-6">
      <StandardRouteDetailHeader
        icon={<IconCircleDollarSign class="size-5" />}
        title={props.bankTransaction.name}
        badge={bankTransactionStatusLabel(props.bankTransaction.status)}
        date={props.bankTransaction.date ?? undefined}
        avatarUrl={props.bankTransaction.logoUrl ?? undefined}
        menuContent={
          <>
            <DropdownMenuItem onClick={() => props.setMode("edit")}>
              <IconPencil class="size-4" />
              Edit
            </DropdownMenuItem>
            <Show when={isApproved()}>
              <DropdownMenuItem
                disabled={isApproving()}
                onClick={handleUnapprove}
              >
                <IconUndo class="size-4" />
                Unapprove
              </DropdownMenuItem>
            </Show>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              class="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <IconTrash class="size-4" />
              Delete
            </DropdownMenuItem>
          </>
        }
      />

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          <StandardRouteDetailLeftColumn>
            <div class="space-y-6">
              <StandardRouteDetailCard
                icon={<IconArrowRightFromLine class="size-5" />}
                title="Bank Transaction Details"
                contentClass=""
                action={
                  <Tooltip>
                    <TooltipTrigger
                      as={Button<"button">}
                      variant="ghost"
                      size="icon"
                      class="size-7 text-muted-foreground hover:text-foreground"
                      aria-label="Edit transaction"
                      onClick={() => props.setMode("edit")}
                    >
                      <IconPencil class="size-4" />
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                }
              >
                {/* Amount is the single most important accounting fact — make it the hero */}
                <div class="pb-5">
                  <p class="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    Amount
                  </p>
                  <div class="mt-1.5 flex items-end gap-2">
                    <p class="text-3xl font-semibold tabular-nums tracking-tight">
                      {formatCurrency(props.bankTransaction.amount, {
                        currency: props.bankTransaction.currencyCode,
                      })}
                    </p>
                    <Show
                      when={
                        props.bankTransaction.unofficialCurrencyCode ??
                        props.bankTransaction.currencyCode
                      }
                    >
                      {(code) => (
                        <Badge
                          variant="outline"
                          class="mb-1.5 px-1.5 py-0.5 font-normal text-muted-foreground"
                        >
                          {code()}
                        </Badge>
                      )}
                    </Show>
                  </div>
                </div>

                <div class="divide-y divide-border border-t">
                  <InfoRow
                    label="Date"
                    value={formatDisplayCalendarDate(
                      props.bankTransaction.date,
                    )}
                  />
                  <InfoRow
                    label="Merchant"
                    value={props.bankTransaction.merchantName ?? null}
                  />
                  <InfoRow
                    label="Category"
                    value={props.bankTransaction.category ?? null}
                  />
                  <InfoRow
                    label="Website"
                    value={props.bankTransaction.website ?? null}
                  />
                </div>

                {/* Allocations — a compact, pretty summary of how the
                    transaction is split across accounts. Each account links
                    to its chart account. The bank account line is omitted
                    since it's always the source account. */}
                <Show when={props.bankTransaction.lines.length > 0}>
                  <div class="pt-5">
                    <div class="mb-2 flex items-center justify-between gap-2">
                      <p class="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                        Allocations
                      </p>
                      <Show when={props.bankTransaction.postedJournalEntryId}>
                        <Link
                          to="/dashboard/journal-entries/$journalEntryId"
                          params={{
                            journalEntryId:
                              props.bankTransaction.postedJournalEntryId!,
                          }}
                          class="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                        >
                          Journal Entry
                          <IconArrowRight class="size-3.5" />
                        </Link>
                      </Show>
                    </div>
                    <JournalEntryLinesSummary
                      lines={props.bankTransaction.lines}
                      bankAccountChartAccountId={
                        props.bankTransaction.bankAccountChartAccountId
                      }
                    />
                  </div>
                </Show>

                {/* Status / channel / type — shown as chips like the tags in
                    the doc detail card */}
                <div class="pt-5">
                  <p class="mb-2 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    Details
                  </p>
                  <div class="flex flex-wrap gap-1.5">
                    <Badge variant="outline" class="gap-1 font-normal">
                      {statusIcon(props.bankTransaction.status)}
                      {bankTransactionStatusLabel(props.bankTransaction.status)}
                    </Badge>
                    <Show
                      when={paymentChannelLabel(
                        props.bankTransaction.paymentChannel,
                      )}
                    >
                      {(label) => (
                        <Badge variant="outline" class="gap-1 font-normal">
                          {paymentChannelIcon(
                            props.bankTransaction.paymentChannel,
                          )}
                          {label()}
                        </Badge>
                      )}
                    </Show>
                  </div>
                </div>
              </StandardRouteDetailCard>

              {/* Personal finance category — pulled into its own card like the
                  file card in the document detail route. */}
              <StandardRouteDetailCard
                icon={<IconTag class="size-5" />}
                title="Personal Finance Category"
                contentClass="divide-y divide-border"
              >
                <InfoRow
                  label="Category"
                  value={
                    props.bankTransaction.pfcPrimary
                      ? pfcPrimaryLabel(props.bankTransaction.pfcPrimary)
                      : null
                  }
                />
                <InfoRow
                  label="Detail"
                  value={
                    props.bankTransaction.pfcDetailed
                      ? pfcDetailedLabel(props.bankTransaction.pfcDetailed)
                      : null
                  }
                />
                <InfoRow
                  label="Confidence"
                  value={
                    props.bankTransaction.pfcConfidence
                      ? pfcConfidenceLabel(props.bankTransaction.pfcConfidence)
                      : null
                  }
                />
              </StandardRouteDetailCard>
            </div>
          </StandardRouteDetailLeftColumn>
          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader>
                <StandardRouteDetailTabs
                  defaultValue="details"
                  class="flex flex-col"
                >
                  <TabsList class="w-full">
                    <TabsTrigger value="details" class="flex-1">
                      <IconInfo class="mr-2 size-4" />
                      <span class="hidden sm:inline">Details</span>
                    </TabsTrigger>
                    <TabsTrigger value="location" class="flex-1">
                      <IconMapPin class="mr-2 size-4" />
                      <span class="hidden sm:inline">Location</span>
                    </TabsTrigger>
                    <TabsTrigger value="provider" class="flex-1">
                      <IconStore class="mr-2 size-4" />
                      <span class="hidden sm:inline">Provider</span>
                    </TabsTrigger>
                    <TabsTrigger value="journal-entry" class="flex-1">
                      <IconCircleDollarSign class="mr-2 size-4" />
                      <span class="hidden sm:inline">Allocations</span>
                    </TabsTrigger>
                    <TabsTrigger value="associations" class="flex-1">
                      <IconLink class="mr-2 size-4" />
                      <span class="hidden sm:inline">Associations</span>
                    </TabsTrigger>
                  </TabsList>

                  <StandardRouteDetailTabsStack>
                    <StandardRouteDetailTabsContent
                      value="details"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <div class="divide-y divide-border">
                          <InfoRow
                            label="Original Description"
                            value={
                              props.bankTransaction.originalDescription ?? null
                            }
                          />
                          <InfoRow
                            label="Institution"
                            value={
                              props.bankTransaction.institutionName ?? null
                            }
                          />
                          <InfoRow
                            label="Bank Account"
                            value={bankAccountLabel()}
                          />
                          <InfoRow
                            label="Created"
                            value={formatDisplayCalendarDate(
                              new Date(props.bankTransaction.createdAt),
                            )}
                          />
                          <InfoRow
                            label="Updated"
                            value={formatDisplayCalendarDate(
                              new Date(props.bankTransaction.updatedAt),
                            )}
                          />
                          <InfoRow
                            label="Reviewed"
                            value={
                              props.bankTransaction.reviewedAt
                                ? formatDisplayCalendarDate(
                                    new Date(props.bankTransaction.reviewedAt),
                                  )
                                : null
                            }
                          />
                        </div>
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="location"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        {/* Deferred Leaflet map — the library is only fetched
                            when this tab is opened (see LocationMap). */}
                        <Show
                          when={locationCoords(
                            props.bankTransaction.addressLat,
                            props.bankTransaction.addressLon,
                            props.bankTransaction.locationLat,
                            props.bankTransaction.locationLon,
                          )}
                        >
                          {(coords) => (
                            <div class="border-b border-border p-4">
                              <div class="h-64 w-full overflow-hidden rounded-lg">
                                <LocationMap
                                  lat={coords().lat}
                                  lon={coords().lon}
                                  label={props.bankTransaction.name}
                                  hasStreet={
                                    !!props.bankTransaction.locationAddress
                                  }
                                  hasCity={!!props.bankTransaction.locationCity}
                                  class="h-full w-full"
                                />
                              </div>
                            </div>
                          )}
                        </Show>
                        <div class="divide-y divide-border">
                          <InfoRow
                            label="Address"
                            value={
                              props.bankTransaction.locationAddress ?? null
                            }
                          />
                          <InfoRow
                            label="City"
                            value={props.bankTransaction.locationCity ?? null}
                          />
                          <InfoRow
                            label="Region"
                            value={props.bankTransaction.locationRegion ?? null}
                          />
                          <InfoRow
                            label="Postal Code"
                            value={
                              props.bankTransaction.locationPostalCode ?? null
                            }
                          />
                          <InfoRow
                            label="Country"
                            value={
                              props.bankTransaction.locationCountry ?? null
                            }
                          />
                          <InfoRow
                            label="Store Number"
                            value={
                              props.bankTransaction.locationStoreNumber ?? null
                            }
                          />
                          <InfoRow
                            label="Coordinates"
                            value={locationCoordsLabel(
                              props.bankTransaction.locationLat,
                              props.bankTransaction.locationLon,
                            )}
                          />
                          <InfoRow
                            label="Geocoded Coordinates"
                            value={locationCoordsLabel(
                              props.bankTransaction.addressLat,
                              props.bankTransaction.addressLon,
                            )}
                          />
                        </div>
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="provider"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <div class="divide-y divide-border">
                          <InfoRow
                            label="Provider"
                            value={props.bankTransaction.provider ?? null}
                          />
                          <InfoRow
                            label="Provider Transaction ID"
                            value={
                              props.bankTransaction.providerTransactionId ??
                              null
                            }
                          />
                          <InfoRow
                            label="Transaction Code"
                            value={
                              props.bankTransaction.transactionCode ?? null
                            }
                          />
                          <InfoRow
                            label="Merchant Category Code (MCC)"
                            value={
                              props.bankTransaction.merchantCategoryCode ?? null
                            }
                          />
                          <InfoRow
                            label="Check Number"
                            value={props.bankTransaction.checkNumber ?? null}
                          />
                          <InfoRow
                            label="Pending Transaction ID"
                            value={
                              props.bankTransaction.pendingTransactionId ?? null
                            }
                          />
                          <InfoRow
                            label="Account Owner"
                            value={props.bankTransaction.accountOwner ?? null}
                          />
                          <InfoRow
                            label="Running Balance"
                            value={
                              props.bankTransaction.runningBalance != null
                                ? formatCurrency(
                                    props.bankTransaction.runningBalance,
                                  )
                                : null
                            }
                          />
                          <InfoRow
                            label="Posted"
                            value={
                              props.bankTransaction.postedDatetime
                                ? formatDisplayCalendarDate(
                                    props.bankTransaction.postedDatetime,
                                  )
                                : null
                            }
                          />
                          <InfoRow
                            label="Merchant Entity ID"
                            value={
                              props.bankTransaction.merchantEntityId ?? null
                            }
                          />
                        </div>
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="journal-entry"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <JournalEntryLinesTable
                          lines={props.bankTransaction.lines}
                        />
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="associations"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <StandardRouteAssociationsPanel
                          source={{
                            type: "bank-transaction",
                            id: props.bankTransaction.id,
                          }}
                          businessId={props.businessId}
                          tabs={[
                            {
                              type: "journal-entry",
                              defaultVisibility: {
                                reference: false,
                                entries: false,
                              },
                            },
                            {
                              type: "document",
                              defaultVisibility: {
                                type: false,
                                amount: false,
                              },
                            },
                            {
                              type: "vendor",
                              defaultVisibility: {
                                address: false,
                                website: false,
                                contactName: false,
                              },
                            },
                            {
                              type: "worksheet",
                              defaultVisibility: {
                                description: false,
                                type: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "fixed-asset",
                              defaultVisibility: {
                                purchaseDate: false,
                                depreciationMethod: false,
                                purchaseCost: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "bank-account",
                              defaultVisibility: {
                                bankName: false,
                                accountCode: false,
                                description: false,
                                updatedAt: false,
                              },
                            },
                          ]}
                        />
                      </CardContent>
                    </StandardRouteDetailTabsContent>
                  </StandardRouteDetailTabsStack>
                </StandardRouteDetailTabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>

      <DeleteBankTransactionDialog
        bankTransaction={props.bankTransaction}
        businessId={props.businessId}
        open={deleteOpen()}
        onOpenChange={setDeleteOpen}
        onDeleted={props.onDeleted}
      />
    </div>
  );
}

// -- Edit / Create Form -------------------------------------------------------

function BankTransactionEdit(props: {
  bankTransaction: BankTransactionItem | null;
  businessId: string;
  allAccounts: { id: string; code: string; name: string }[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isNew = () => props.bankTransaction === null;
  const [serverError, setServerError] = createSignal("");

  const form = useBankTransactionForm(props.businessId, props.bankTransaction, {
    bankAccountChartAccountId: props.bankTransaction?.bankAccountChartAccountId,
    amount: props.bankTransaction?.amount,
    onSuccess: () => {
      toast.success(
        isNew()
          ? "Bank transaction created successfully"
          : "Bank transaction updated successfully",
      );
      props.onSaved();
    },
    onError: (e) =>
      setServerError(extractErrorMessage(e, "An unexpected error occurred")),
  });

  return (
    <StandardRouteDetailEditCard
      title={isNew() ? "Add Bank Transaction" : "Edit Bank Transaction"}
      description={
        isNew()
          ? "Create a new bank transaction record."
          : "Update the bank transaction details. Changes apply immediately on save."
      }
      formId="bank-transaction-edit-form"
      onCancel={props.onCancel}
      saveButton={
        <form.Subscribe
          selector={(s) => ({
            canSubmit: s.canSubmit,
            isSubmitting: s.isSubmitting,
          })}
        >
          {(state) => (
            <Button
              type="submit"
              form="bank-transaction-edit-form"
              disabled={!state().canSubmit || state().isSubmitting}
            >
              {state().isSubmitting
                ? "Saving..."
                : isNew()
                  ? "Add Bank Transaction"
                  : "Save Changes"}
            </Button>
          )}
        </form.Subscribe>
      }
    >
      <form
        id="bank-transaction-edit-form"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Grid cols={1} colsMd={2} colsLg={3} class="gap-4">
          <Col span={1} spanLg={2}>
            <form.Field name="name">
              {(field) => (
                <FormField label="Name" required field={field()}>
                  <TextFieldInput
                    value={field().state.value}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Bank transaction name"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="status">
              {(field) => (
                <FormField label="Status" field={field()}>
                  <Select
                    value={field().state.value || undefined}
                    onChange={(v) => v && field().handleChange(v)}
                    options={["pending", "approved", "skipped"]}
                    placeholder="Select a status"
                    itemComponent={(p) => (
                      <SelectItem item={p.item}>
                        {bankTransactionStatusLabel(p.item.rawValue)}
                      </SelectItem>
                    )}
                  >
                    <SelectTrigger>
                      <SelectValue<string>>
                        {(state) =>
                          bankTransactionStatusLabel(state.selectedOption())
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent />
                  </Select>
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="merchantName">
              {(field) => (
                <FormField label="Merchant" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Optional merchant name"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="category">
              {(field) => (
                <FormField label="Category" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Optional category"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
        </Grid>

        <Separator class="my-6" />

        {/* Journal entry lines — shared editor (same as the JE form). These
            become the hard-linked JE's lines when the transaction is
            approved. */}
        <JournalEntryLinesEditor
          Field={form.Field}
          accountOptions={props.allAccounts.map((a) => ({
            label: `${a.code} - ${a.name}`,
            value: a.id,
          }))}
          description="These lines are posted to the linked journal entry when you approve this transaction."
        />

        {/* Balance validation error — surfaced from the form schema's
            superRefine so the user sees why they can't save. Re-validates on
            change, so it clears as soon as the lines balance. */}
        <form.Field name="lines">
          {(field) => (
            <Show when={field().state.meta.errors.length > 0}>
              <p class="mt-2 text-sm text-destructive">
                {fieldErrorText(field().state.meta.errors[0])}
              </p>
            </Show>
          )}
        </form.Field>

        <Show when={serverError()}>
          <p class="mt-4 text-sm text-destructive">{serverError()}</p>
        </Show>
      </form>
    </StandardRouteDetailEditCard>
  );
}

/** Extract a human-readable message from a form field error, which may be a
 *  plain string or a zod issue object with a `message` property. */
function fieldErrorText(
  error: string | { message: string } | undefined,
): string | undefined {
  if (!error) return undefined;
  return typeof error === "string" ? error : error.message;
}

function FormField(props: {
  label: string;
  required?: boolean;
  field: {
    state: { meta: { errors: (string | { message: string } | undefined)[] } };
  };
  children: JSX.Element;
}) {
  const hasError = () => props.field.state.meta.errors.length > 0;
  const errorMessage = () => {
    const e = props.field.state.meta.errors[0];
    if (!e) return undefined;
    return typeof e === "string" ? e : e.message;
  };
  return (
    <TextField validationState={hasError() ? "invalid" : "valid"}>
      <TextFieldLabel>
        {props.label}
        <Show when={props.required}>
          <span class="text-destructive ml-0.5">*</span>
        </Show>
      </TextFieldLabel>
      {props.children}
      <TextFieldErrorMessage>{errorMessage()}</TextFieldErrorMessage>
    </TextField>
  );
}

function locationCoordsLabel(
  lat: number | undefined,
  lon: number | undefined,
): string | null {
  if (lat == null || lon == null) return null;
  return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
}

/** Title-case a raw provider value (e.g. `in store` → `In Store`). */
function titleCase(value: string): string {
  return value
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Human-readable label for the payment channel (`online` | `in store` |
 * `other`). Returns null when absent.
 */
function paymentChannelLabel(
  channel: string | null | undefined,
): string | null {
  if (!channel) return null;
  return titleCase(channel);
}

/**
 * Icon for the payment channel — a house for in-store purchases, a globe for
 * online, and a store as a fallback for anything else.
 */
function paymentChannelIcon(channel: string | null | undefined): JSX.Element {
  const normalized = channel?.toLowerCase();
  if (normalized === "in store") {
    return <IconHouse class="size-3.5" />;
  }
  if (normalized === "online") {
    return <IconGlobe class="size-3.5" />;
  }
  return <IconStore class="size-3.5" />;
}

/**
 * Icon for the review status — a clock for pending, a checkmark for approved,
 * and an X for skipped.
 */
function statusIcon(status: string | null | undefined): JSX.Element {
  if (status === "pending") {
    return <IconClock class="size-3.5" />;
  }
  if (status === "skipped") {
    return <IconCircleX class="size-3.5" />;
  }
  return <IconCircleCheck class="size-3.5" />;
}

/**
 * Resolve the best available coordinates for the map. Prefers the geocoded
 * address coords (more reliable than the provider's lat/lon), falling back to
 * the provider's location coords. Returns null when neither is present.
 */
function locationCoords(
  addressLat: number | undefined,
  addressLon: number | undefined,
  locationLat: number | undefined,
  locationLon: number | undefined,
): { lat: number; lon: number } | null {
  if (addressLat != null && addressLon != null)
    return { lat: addressLat, lon: addressLon };
  if (locationLat != null && locationLon != null)
    return { lat: locationLat, lon: locationLon };
  return null;
}

/** Compact, pretty summary of the bank transaction's allocations — used in
 *  the prime card. Shows just the account and its value (the bank account
 *  line is omitted since it's always the source account). */
function JournalEntryLinesSummary(props: {
  lines: BankTransactionLineItem[];
  bankAccountChartAccountId?: string;
}) {
  // Drop the bank account line — it's always the source account, so showing
  // it adds noise. The remaining lines are the actual allocations.
  const allocations = () =>
    props.lines.filter((l) => l.accountId !== props.bankAccountChartAccountId);

  if (allocations().length === 0) {
    return (
      <p class="text-sm text-muted-foreground">
        No allocations yet. Add them in edit mode.
      </p>
    );
  }

  return (
    <ul class="space-y-2">
      <Index each={allocations()}>
        {(line) => (
          <li class="flex items-center justify-between gap-3">
            <Link
              to="/dashboard/accounts/$accountId"
              params={{ accountId: line().accountId }}
              class="min-w-0 truncate text-sm font-medium text-primary hover:underline"
            >
              <Show
                when={line().accountCode}
                fallback={line().accountName ?? line().accountId}
              >
                {`${line().accountCode} - ${line().accountName}`}
              </Show>
            </Link>
            <span class="shrink-0 font-mono text-sm tabular-nums text-foreground">
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format((line().debit ?? 0) + (line().credit ?? 0))}
            </span>
          </li>
        )}
      </Index>
    </ul>
  );
}

/** Read-only table of the bank transaction's journal entry lines. */
function JournalEntryLinesTable(props: { lines: BankTransactionLineItem[] }) {
  const totalDebits = () => props.lines.reduce((s, e) => s + (e.debit ?? 0), 0);
  const totalCredits = () =>
    props.lines.reduce((s, e) => s + (e.credit ?? 0), 0);

  if (props.lines.length === 0) {
    return (
      <p class="pb-2 text-sm text-muted-foreground">
        No lines on this journal entry.
      </p>
    );
  }

  return (
    <div class="overflow-x-auto rounded-md border">
      <table class="w-full text-sm">
        <thead class="border-b bg-muted/50">
          <tr>
            <th class="px-3 py-2 text-left font-medium">Account</th>
            <th class="w-32 px-3 py-2 text-right font-medium">Debit</th>
            <th class="w-32 px-3 py-2 text-right font-medium">Credit</th>
            <th class="px-3 py-2 text-left font-medium">Memo</th>
          </tr>
        </thead>
        <tbody>
          <Index each={props.lines}>
            {(line) => (
              <tr class="border-b last:border-0">
                <td class="px-3 py-2">
                  <Link
                    to="/dashboard/accounts/$accountId"
                    params={{ accountId: line().accountId }}
                    class="font-medium text-primary hover:underline"
                  >
                    <Show when={line().accountCode} fallback={line().accountId}>
                      {`${line().accountCode} - ${line().accountName}`}
                    </Show>
                  </Link>
                </td>
                <td class="px-3 py-2 text-right font-mono">
                  {line().debit != null
                    ? new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(line().debit!)
                    : ""}
                </td>
                <td class="px-3 py-2 text-right font-mono">
                  {line().credit != null
                    ? new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(line().credit!)
                    : ""}
                </td>
                <td class="px-3 py-2">{line().description ?? ""}</td>
              </tr>
            )}
          </Index>
          <tr class="bg-muted/30 text-sm font-semibold">
            <td class="px-3 py-2">Totals</td>
            <td class="px-3 py-2 text-right font-mono">
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(totalDebits())}
            </td>
            <td class="px-3 py-2 text-right font-mono">
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(totalCredits())}
            </td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function InfoRow(props: {
  label: string;
  value: string | null | undefined;
  multiline?: boolean;
}) {
  if (!props.value) return null;

  if (props.label === "Website") {
    const href = props.value.startsWith("http")
      ? props.value
      : `https://${props.value}`;
    return (
      <div class="flex items-center justify-between py-3.5 gap-4">
        <p class="text-sm text-muted-foreground">{props.label}</p>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm font-semibold text-primary hover:underline"
        >
          {props.value.replace(/^https?:\/\//, "")}
        </a>
      </div>
    );
  }

  return (
    <div
      class={
        props.multiline
          ? "py-3.5 space-y-1.5"
          : "flex items-center justify-between py-3.5 gap-4"
      }
    >
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6
        class={
          props.multiline
            ? "text-sm font-medium whitespace-pre-wrap"
            : "text-sm font-semibold text-right"
        }
      >
        {props.value}
      </h6>
    </div>
  );
}
